/**
 * Shared "how long ago" formatter for the web SPA.
 *
 * Single source of truth for the compact relative stamp rendered next to a
 * timestamp across the dashboard ("just now", "3m ago", "5h ago", "2d ago"):
 * the sales Leads table + Today list, the header notification bell, the
 * support Conversations list, and the ticket-detail sidebar. Beyond a week
 * it falls back to an absolute locale date — an old lead reads "31/05/2026",
 * which is more useful than "412d ago".
 *
 * Accepts an ISO string OR a `Date`. The ticket-detail sidebar already holds
 * a `Date` (it builds one for the `title` hover tooltip) and passes it
 * straight through; everywhere else passes the raw ISO string from the API.
 *
 * Semantics (the canonical buckets every per-component copy collapsed onto):
 *   - empty / missing / null input     → "" (caller decides the placeholder)
 *   - unparseable date or future date  → "" (clock skew shouldn't print "-3m ago")
 *   - < 1 min                          → "just now"
 *   - < 60 min                         → "{n}m ago"
 *   - < 24 h                           → "{n}h ago"
 *   - < 7 d                            → "{n}d ago"
 *   - otherwise                        → localized date (`toLocaleDateString`)
 *
 * `now` is injectable purely so unit tests are deterministic; callers pass
 * nothing and get `Date.now()`.
 */
export function relativeTime(
  input: string | Date | null | undefined,
  now: number = Date.now(),
): string {
  // `== null` covers null + undefined; "" is the empty-ISO case. A Date is
  // never == null and never === "", so it falls through to be handled below.
  if (input == null || input === "") return "";
  const date = input instanceof Date ? input : new Date(input);
  const then = date.getTime();
  if (Number.isNaN(then)) return "";
  const ms = now - then;
  // A future timestamp (negative age) is almost always clock skew between
  // the agent box and the operator's machine — render nothing rather than
  // a misleading "-2m ago".
  if (ms < 0) return "";
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return date.toLocaleDateString();
}
